import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db, auth } from '../../firebase';
import { ref, get, update, remove } from 'firebase/database';
import { useDashboardChrome } from '../../hooks/useDashboardChrome';
import { botIcon, botLabel, generateWebhookSecret, widgetURL, API_BASE, TONE_OPTIONS, type Bot } from '../../utils/botHelpers';
import SendInviteSection from './SendInviteSection';

interface Props {
  botId: string;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}

export default function BotSettingsModal({ botId, onClose, onSaved, onDeleted }: Props) {
  const { currentUser, refreshBots } = useDashboardChrome();
  const [editingBot, setEditingBot] = useState<Bot | null>(null);
  const [loadingBot, setLoadingBot] = useState(true);

  const [metaConnectStatus, setMetaConnectStatus] = useState<{ name: string; phone?: string } | null>(null);
  const [metaConnecting, setMetaConnecting] = useState(false);
  const [metaPhoneNumbers, setMetaPhoneNumbers] = useState<Array<{ id: string; display_phone_number: string; verified_name: string }>>([]);
  const [metaWaOAuthStatus, setMetaWaOAuthStatus] = useState<'idle' | 'loading' | 'numbers' | 'done'>('idle');
  const [fbReconnecting, setFbReconnecting] = useState(false);
  const [fbReconnectPages, setFbReconnectPages] = useState<Array<{ id: string; name: string; access_token: string }>>([]);
  const [fbReconnectStatus, setFbReconnectStatus] = useState<'idle' | 'loading' | 'pages' | 'done'>('idle');
  const [settingsDatasets, setSettingsDatasets] = useState<Array<{ id: string; name: string; format: string }>>([]);
  const [showLegacyKB, setShowLegacyKB] = useState(false);
  const [newWebsiteInput, setNewWebsiteInput] = useState('');
  const [waSettingsMode, setWaSettingsMode] = useState<'test_number' | 'meta_connect' | 'manual'>('meta_connect');
  const [switchingMode, setSwitchingMode] = useState(false);

  // Fresh fetch on open — matches the gear icon / per-bot-type "Settings"
  // buttons' prior behavior of never reusing the (narrower) bots-list record.
  useEffect(() => {
    let alive = true;
    setLoadingBot(true);
    get(ref(db, `bots/${botId}`)).then(snap => {
      if (!alive) return;
      setEditingBot(snap.exists() ? { id: botId, ...snap.val() } : null);
      setLoadingBot(false);
    });
    return () => { alive = false; };
  }, [botId]);

  // Load the Facebook SDK for WhatsApp Embedded Signup — idempotent (guarded
  // by window.FB), safe to run every time this modal mounts.
  useEffect(() => {
    if ((window as any).FB) return;
    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.onload = () => {
      (window as any).FB.init({ appId: process.env.REACT_APP_FACEBOOK_APP_ID || '', version: 'v21.0', xfbml: false, cookie: true });
    };
    document.head.appendChild(script);
  }, []);

  // Capture WABA/phone data from Embedded Signup wizard
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== 'https://www.facebook.com' && event.origin !== 'https://web.facebook.com') return;
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data.type !== 'WA_EMBEDDED_SIGNUP') return;
        if (data.event === 'FINISH') {
          const { phone_number_id } = data.data || {};
          if (phone_number_id) setEditingBot(prev => prev ? { ...prev, phoneNumberId: phone_number_id } as any : prev);
        } else if (data.event === 'CANCEL') {
          console.log('[Embedded Signup] Cancelled at step:', data.data?.current_step);
        } else if (data.event === 'ERROR') {
          console.error('[Embedded Signup] Error:', data.data?.error_message);
        }
      } catch (_) {}
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Init WhatsApp connection mode once the bot record loads
  useEffect(() => {
    if (!editingBot) return;
    const isWA = editingBot.botType === 'whatsapp' || (!editingBot.botType && !!editingBot.phoneNumberId);
    if (!isWA) return;
    const savedMode = (editingBot as any).whatsappMode;
    if ((editingBot as any).isTestNumber) setWaSettingsMode('test_number');
    else if (savedMode === 'meta_connect' || savedMode === 'manual') setWaSettingsMode(savedMode);
    else if ((editingBot as any).phoneNumberId) setWaSettingsMode('manual'); // legacy bots saved before whatsappMode was tracked
    else setWaSettingsMode('meta_connect');

    if (savedMode === 'meta_connect' && (editingBot as any).phoneNumberId) {
      setMetaConnectStatus({
        name: (editingBot as any).whatsappVerifiedName || (editingBot as any).businessName,
        phone: (editingBot as any).whatsappDisplayNumber,
      });
    } else {
      setMetaConnectStatus(null);
    }
    setMetaWaOAuthStatus('idle');
    setMetaPhoneNumbers([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingBot?.id]);

  // Reset Facebook reconnect state when the loaded bot changes
  useEffect(() => {
    if (!editingBot) return;
    setFbReconnectStatus('idle');
    setFbReconnectPages([]);
  }, [editingBot?.id]);

  // Load dataset list for AI, email, or comments-mode Facebook bots
  useEffect(() => {
    const isCommentsBot = editingBot?.botType === 'facebook' && (editingBot as any)?.fbMode === 'comments';
    if ((editingBot?.botType !== 'ai' && (editingBot as any)?.botType !== 'email' && !isCommentsBot) || !currentUser?.uid) return;
    setShowLegacyKB(false);
    get(ref(db, `datasets/${currentUser.uid}`)).then(snap => {
      const list: Array<{ id: string; name: string; format: string }> = [];
      if (snap.exists()) snap.forEach(c => { list.push({ id: c.key!, name: c.val().name, format: c.val().format }); });
      setSettingsDatasets(list);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingBot?.id, currentUser?.uid]);

  // AI <-> flow-builder mode switch. The bot's "channel" is whatsapp/facebook/
  // website normally held directly in botType - an AI-mode bot instead holds
  // botType:'ai' with the real channel moved to aiChannel, since that's the
  // one field every message handler across whatsapp.js/facebook.js/website.js
  // already branches on. Switching preserves whichever channel it's already
  // on (phoneNumberId/fbPageId/websiteUrl are untouched either way) and never
  // touches aiConfig, so flipping back to AI later keeps whatever knowledge
  // base/model was configured before.
  const currentChannel = editingBot?.botType === 'ai' ? (editingBot as any).aiChannel : editingBot?.botType;
  const isComments = editingBot?.botType === 'facebook' && (editingBot as any)?.fbMode === 'comments';
  const canSwitchMode = !!editingBot && !isComments && ['whatsapp', 'facebook', 'website'].includes(currentChannel || '');
  const isAIMode = editingBot?.botType === 'ai';

  const handleSwitchMode = async () => {
    if (!editingBot || !currentChannel) return;
    const target = isAIMode ? 'flow' : 'AI';
    if (!window.confirm(
      isAIMode
        ? `Switch this bot back to flow-builder mode? It will stop answering with AI and use your flow instead.`
        : `Switch this bot to AI mode? It will stop using your flow and answer with AI from its knowledge base instead.`
    )) return;
    setSwitchingMode(true);
    try {
      const updates: any = isAIMode
        ? { botType: currentChannel, aiChannel: null }
        : { botType: 'ai', aiChannel: currentChannel, aiConfig: (editingBot as any).aiConfig || { model: 'openai/gpt-oss-20b' } };
      await update(ref(db, `bots/${editingBot.id}`), updates);
      setEditingBot({ ...editingBot, ...updates } as any);
      await refreshBots();
    } catch (e) {
      alert(`Could not switch to ${target} mode. Please try again.`);
    } finally {
      setSwitchingMode(false);
    }
  };

  const handleUpdateBot = async () => {
    if (!editingBot) return;
    const isWA = editingBot.botType === 'whatsapp' || (!editingBot.botType && !!editingBot.phoneNumberId);
    const isFb = editingBot.botType === 'facebook';
    try {
      let resolvedTestKeyword = (editingBot as any).testKeyword || '';
      let resolvedTestDisplayNumber = (editingBot as any).testDisplayNumber || null;
      if (isWA && waSettingsMode === 'test_number' && !resolvedTestKeyword) {
        const r = await fetch(`${API_BASE}/api/test-number/info?name=${encodeURIComponent(editingBot.businessName)}`);
        const d = await r.json();
        resolvedTestKeyword = d.keyword || editingBot.businessName.toLowerCase().replace(/[^a-z0-9]/g, '').substring(0, 10) || 'bot';
        resolvedTestDisplayNumber = d.displayNumber || null;
      }
      // accessToken lives at botSecrets/{id}, not bots/{id} (that node is
      // broadly readable by any logged-in user for the dashboard list query,
      // so a real credential can't live there) - editingBot.accessToken is
      // populated ONLY when the user typed a new value or just reconnected
      // via OAuth this session, never from the initial load. Write it to
      // botSecrets separately and never include it in the bots/{id} update.
      const newAccessToken = ((editingBot as any).accessToken || '').trim();
      if (newAccessToken) await update(ref(db, `botSecrets/${editingBot.id}`), { accessToken: newAccessToken });
      const needsManualTokenCheck = isWA && waSettingsMode === 'manual' && !!newAccessToken;
      await update(ref(db, `bots/${editingBot.id}`), {
        businessName: editingBot.businessName,
        welcomeMessage: editingBot.welcomeMessage,
        allowedWebsites: editingBot.allowedWebsites || [],
        ...(isWA ? {
          whatsappMode: waSettingsMode,
          isTestNumber: waSettingsMode === 'test_number',
          testKeyword: waSettingsMode === 'test_number' ? resolvedTestKeyword : null,
          testDisplayNumber: waSettingsMode === 'test_number' ? resolvedTestDisplayNumber : null,
          phoneNumberId: waSettingsMode !== 'test_number' ? ((editingBot as any).phoneNumberId || '') : '',
          whatsappDisplayNumber: waSettingsMode === 'meta_connect' ? ((editingBot as any).whatsappDisplayNumber || null) : null,
          whatsappVerifiedName: waSettingsMode === 'meta_connect' ? ((editingBot as any).whatsappVerifiedName || null) : null,
        } : {
          phoneNumberId: (editingBot as any).phoneNumberId || '',
          ...(isFb ? { fbPageId: (editingBot as any).fbPageId || '' } : {}),
        }),
        paymentMethods: (editingBot as any).paymentMethods || null,
        aiConfig: (editingBot as any).aiConfig || null,
        emailUser: (editingBot as any).emailUser || '',
        emailPass: (editingBot as any).emailPass || '',
        emailFrom: (editingBot as any).emailFrom || '',
        imapHost: (editingBot as any).imapHost || '',
        imapPort: (editingBot as any).imapPort || 993,
        imapSecure: (editingBot as any).imapSecure !== false,
        smtpHost: (editingBot as any).smtpHost || '',
        smtpPort: (editingBot as any).smtpPort || 587,
        smtpSecure: !!(editingBot as any).smtpSecure,
        updatedAt: Date.now(),
      });

      // A manually-pasted token could be stale, wrong, or issued for a
      // different Meta app entirely - it would be accepted here with no
      // feedback and only fail unpredictably later in real conversations
      // (this is exactly what happened to the official Webiconic bot).
      // Validate it against Meta right away instead of finding out live.
      if (needsManualTokenCheck) {
        const idToken = await auth.currentUser!.getIdToken();
        const subRes = await fetch(`${API_BASE}/api/bots/${editingBot.id}/self-setup-subscribe`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${idToken}` },
        }).then(r => r.json()).catch(() => ({ success: false, error: 'Could not reach the server to validate the token.' }));
        if (!subRes.success) {
          await refreshBots();
          onSaved();
          alert(`Saved, but this WhatsApp connection needs attention:\n\n${subRes.error || 'Could not validate the access token.'}`);
          return;
        }
      }

      await refreshBots();
      onSaved();
      alert('Saved!');
    } catch {
      alert('Error saving changes');
    }
  };

  const connectMetaAccount = () => {
    const FB = (window as any).FB;
    if (!FB) { alert('Facebook SDK not loaded. Please refresh and try again.'); return; }
    setMetaConnecting(true);
    setMetaWaOAuthStatus('loading');

    const configId = process.env.REACT_APP_META_CONFIG_ID;
    const loginOptions: any = configId
      ? { config_id: configId, response_type: 'code', override_default_response_type: true, extras: { feature: 'whatsapp_embedded_signup' } }
      : { scope: 'business_management,whatsapp_business_management' };

    FB.login((response: any) => {
      if (!response.authResponse) {
        setMetaConnecting(false);
        setMetaWaOAuthStatus('idle');
        return;
      }
      const body: any = configId
        ? { code: response.authResponse.code }
        : { accessToken: response.authResponse.accessToken };
      auth.currentUser!.getIdToken()
        .then((idToken: string) => fetch(`${API_BASE}/api/bots/meta-exchange`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
          body: JSON.stringify(body),
        }))
        .then((r: Response) => r.json())
        .then((data: any) => {
          setMetaConnecting(false);
          if (!data.success) {
            setMetaWaOAuthStatus('idle');
            const msg = data.friendly || data.error || 'Meta connection failed. Please try again.';
            alert(data.action && data.actionUrl ? `${msg}\n\n${data.action}: ${data.actionUrl}` : msg);
            return;
          }
          setEditingBot(prev => prev ? { ...prev, accessToken: data.longLivedToken } as any : prev);
          setMetaPhoneNumbers(data.phoneNumbers || []);
          setMetaWaOAuthStatus(data.phoneNumbers?.length ? 'numbers' : 'done');
        })
        .catch(() => {
          setMetaConnecting(false);
          setMetaWaOAuthStatus('idle');
          alert('Connection failed. Please check your internet and try again.');
        });
    }, loginOptions);
  };

  const connectFacebookPage = () => {
    const FB = (window as any).FB;
    if (!FB) { alert('Facebook SDK not loaded. Please refresh and try again.'); return; }
    setFbReconnecting(true);
    setFbReconnectStatus('loading');

    const scope = (editingBot as any)?.fbMode === 'comments'
      ? 'pages_show_list,pages_messaging,pages_manage_metadata,pages_manage_engagement,pages_manage_posts'
      : 'pages_show_list,pages_messaging,pages_manage_metadata,pages_manage_posts';

    FB.login((response: any) => {
      if (!response.authResponse) {
        setFbReconnecting(false);
        setFbReconnectStatus('idle');
        return;
      }
      auth.currentUser!.getIdToken()
        .then((idToken: string) => fetch(`${API_BASE}/api/bots/facebook-page-exchange`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
          body: JSON.stringify({ accessToken: response.authResponse.accessToken }),
        }))
        .then((r: Response) => r.json())
        .then((data: any) => {
          setFbReconnecting(false);
          if (!data.success) {
            setFbReconnectStatus('idle');
            const msg = data.friendly || data.error || 'Facebook connection failed. Please try again.';
            alert(data.action && data.actionUrl ? `${msg}\n\n${data.action}: ${data.actionUrl}` : msg);
            return;
          }
          const pages = data.pages || [];
          const match = pages.find((p: any) => p.id === (editingBot as any)?.fbPageId);
          if (match) {
            setEditingBot(prev => prev ? { ...prev, fbPageId: match.id, accessToken: match.access_token } as any : prev);
            setFbReconnectStatus('done');
          } else {
            setFbReconnectPages(pages);
            setFbReconnectStatus('pages');
          }
        })
        .catch(() => {
          setFbReconnecting(false);
          setFbReconnectStatus('idle');
          alert('Connection failed. Please check your internet and try again.');
        });
    }, { scope });
  };

  const handleSelectFbReconnectPage = (page: { id: string; name: string; access_token: string }) => {
    setEditingBot(prev => prev ? { ...prev, fbPageId: page.id, accessToken: page.access_token } as any : prev);
    setFbReconnectStatus('done');
  };

  const handleDeleteBot = async () => {
    if (!window.confirm('Delete this bot permanently? All conversations, contacts, campaigns and analytics will be erased. This cannot be undone.')) return;
    try {
      await Promise.all([
        remove(ref(db, `bots/${botId}`)),
        remove(ref(db, `flows/${botId}`)),
        remove(ref(db, `analytics/${botId}`)),
        remove(ref(db, `conversations/${botId}`)),
        remove(ref(db, `contacts/${botId}`)),
        remove(ref(db, `campaigns/${botId}`)),
        remove(ref(db, `campaignLists/${botId}`)),
        remove(ref(db, `campaignListContacts/${botId}`)),
        remove(ref(db, `campaignUnsubscribes/${botId}`)),
        remove(ref(db, `emailLogs/${botId}`)),
        remove(ref(db, `agentMode/${botId}`)),
      ]);
      await refreshBots();
      onDeleted();
    } catch (err: any) {
      console.error('Delete bot error:', err);
      alert('Error deleting bot: ' + (err?.message || err));
    }
  };

  if (loadingBot || !editingBot) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
        <div style={{ background: 'var(--bg-card)', borderRadius: '16px', padding: '28px', color: 'var(--text-faint)', fontSize: '13px' }}>
          {loadingBot ? 'Loading…' : 'Bot not found.'}
        </div>
      </div>
    );
  }

  const webhookUrl = `${API_BASE}/webhook/${editingBot.id}/${generateWebhookSecret(editingBot.id, editingBot.businessName)}`;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--bg-card)', borderRadius: '16px', border: '1px solid var(--card-border)', padding: '28px', maxWidth: '500px', width: '90%', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ color: 'var(--text-primary)', fontSize: '17px', fontWeight: '700', margin: 0 }}>Bot settings</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-faint)', cursor: 'pointer', fontSize: '18px' }}>✕</button>
        </div>

        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'var(--bg-element)', padding: '4px 12px', borderRadius: '20px', marginBottom: '20px' }}>
          <span style={{ fontSize: '14px' }}>{botIcon(editingBot)}</span>
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{botLabel(editingBot)} bot</span>
        </div>

        {canSwitchMode && (
          <div style={{ marginBottom: '20px', padding: '12px 14px', background: 'var(--bg-element)', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '2px' }}>Response mode</div>
              <div style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: '600' }}>
                {isAIMode ? '🤖 AI — answers from knowledge base' : '🔀 Flow — answers from your flow'}
              </div>
            </div>
            <button
              onClick={handleSwitchMode}
              disabled={switchingMode}
              style={{ padding: '7px 14px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '12px', fontWeight: '600', cursor: switchingMode ? 'not-allowed' : 'pointer' }}
            >
              {switchingMode ? 'Switching…' : `Switch to ${isAIMode ? 'Flow' : 'AI'} mode`}
            </button>
          </div>
        )}

        {[
          { label: 'Business Name', key: 'businessName', type: 'text', placeholder: 'My Business' },
          { label: 'Welcome Message', key: 'welcomeMessage', type: 'textarea', placeholder: 'Hello! How can I help?' },
        ].map(field => (
          <div key={field.key} style={{ marginBottom: '14px' }}>
            <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>{field.label}</label>
            {field.type === 'textarea' ? (
              <textarea value={(editingBot as any)[field.key]} onChange={e => setEditingBot({ ...editingBot, [field.key]: e.target.value })} rows={3}
                style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
            ) : (
              <input type={field.type} value={(editingBot as any)[field.key] || ''} onChange={e => setEditingBot({ ...editingBot, [field.key]: e.target.value })} placeholder={field.placeholder}
                style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
            )}
          </div>
        ))}

        {(editingBot.botType === 'whatsapp' || !editingBot.botType) && (
          <>
            {/* ── Connection mode toggle ── */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>WhatsApp Connection</label>
              <div style={{ display: 'flex', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '10px', padding: '4px', gap: '4px' }}>
                {([
                  { id: 'meta_connect', label: '🔗 Meta Business' },
                  { id: 'manual', label: '⚙️ Manual' },
                  { id: 'test_number', label: '🧪 Test Number' },
                ] as const).map(m => (
                  <button key={m.id} onClick={() => setWaSettingsMode(m.id)}
                    style={{ flex: 1, padding: '8px 4px', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', fontFamily: 'inherit',
                      background: waSettingsMode === m.id ? '#5B4FE8' : 'transparent',
                      color: waSettingsMode === m.id ? 'white' : 'var(--text-muted)',
                      transition: 'all 0.15s',
                    }}>{m.label}</button>
                ))}
              </div>
            </div>

            {/* ── Test Number section ── */}
            {waSettingsMode === 'test_number' && (() => {
              const kw = (editingBot as any).testKeyword || '';
              const dn = ((editingBot as any).testDisplayNumber || '').replace(/\D/g, '');
              const inviteLink = dn && kw ? `https://wa.me/${dn}?text=${encodeURIComponent(kw)}` : null;
              return (
                <div style={{ marginBottom: '18px', padding: '14px', background: 'var(--bg-page)', borderRadius: '10px', border: '1px solid var(--card-border)' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>Shared Test Number</div>
                  {kw ? (
                    <>
                      <div style={{ marginBottom: '10px' }}>
                        <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginBottom: '4px' }}>Your keyword</div>
                        <code style={{ background: 'var(--bg-card)', padding: '4px 12px', borderRadius: '6px', fontSize: '14px', color: '#818cf8', fontWeight: '700' }}>{kw}</code>
                      </div>
                      {inviteLink && (
                        <div>
                          <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginBottom: '4px' }}>Invite link — share with customers</div>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <code style={{ flex: 1, background: 'var(--bg-card)', padding: '6px 10px', borderRadius: '6px', fontSize: '11px', color: 'var(--text-secondary)', wordBreak: 'break-all' }}>{inviteLink}</code>
                            <button onClick={() => { navigator.clipboard.writeText(inviteLink); alert('Copied!'); }}
                              style={{ background: '#5B4FE8', color: 'white', border: 'none', borderRadius: '6px', padding: '6px 12px', fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap' }}>Copy</button>
                          </div>
                          <SendInviteSection botId={editingBot?.id || ''} userName={currentUser?.displayName || currentUser?.email || 'a user'} />
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>A unique keyword will be assigned when you save.</div>
                  )}
                  <div style={{ marginTop: '10px', fontSize: '11px', color: 'var(--text-faint)' }}>
                    Customers message the shared Autopilot number with your keyword to activate this bot. Switch to Meta Business or Manual when you're ready to use your own number.
                  </div>
                </div>
              );
            })()}

            {/* ── Meta Business Connect section ── */}
            {waSettingsMode === 'meta_connect' && (
              <div style={{ marginBottom: '18px', padding: '14px', background: 'var(--bg-page)', borderRadius: '10px', border: '1px solid var(--card-border)' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>Meta Business Account</div>
                {metaConnectStatus ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '20px' }}>✅</span>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>{metaConnectStatus.name}</div>
                      {metaConnectStatus.phone && <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{metaConnectStatus.phone}</div>}
                    </div>
                    <button onClick={() => { setMetaConnectStatus(null); setMetaWaOAuthStatus('idle'); setMetaPhoneNumbers([]); }} style={{ marginLeft: 'auto', background: 'none', border: '1px solid var(--card-border)', color: 'var(--text-muted)', padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}>Disconnect</button>
                  </div>
                ) : metaWaOAuthStatus === 'numbers' && metaPhoneNumbers.length > 0 ? (
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Select your WhatsApp Business number:</div>
                    {metaPhoneNumbers.map(num => (
                      <button key={num.id} onClick={() => {
                        setEditingBot(prev => prev ? { ...prev, phoneNumberId: num.id, whatsappDisplayNumber: num.display_phone_number, whatsappVerifiedName: num.verified_name } as any : prev);
                        setMetaConnectStatus({ name: num.verified_name, phone: num.display_phone_number });
                        setMetaWaOAuthStatus('done');
                      }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', marginBottom: '6px', background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '13px' }}>
                        <div style={{ fontWeight: '600' }}>{num.verified_name}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{num.display_phone_number}</div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <button onClick={connectMetaAccount} disabled={metaConnecting}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#1877F2', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 16px', cursor: metaConnecting ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '600', opacity: metaConnecting ? 0.7 : 1 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    {metaConnecting ? 'Connecting…' : 'Connect WhatsApp Business'}
                  </button>
                )}
                <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '8px' }}>
                  Opens the Meta Business wizard to link your WhatsApp Business account.
                </div>
              </div>
            )}

            {/* ── Manual credentials section ── */}
            {waSettingsMode === 'manual' && (
              <>
                {[
                  { label: 'Phone Number ID', key: 'phoneNumberId', placeholder: '1234567890' },
                  { label: 'Access Token', key: 'accessToken', placeholder: 'Already connected — leave blank to keep', type: 'password' },
                ].map(field => (
                  <div key={field.key} style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>{field.label}</label>
                    <input type={field.type || 'text'} value={(editingBot as any)[field.key] || ''} onChange={e => setEditingBot({ ...editingBot, [field.key]: e.target.value })} placeholder={field.placeholder}
                      style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                ))}
              </>
            )}

            {/* ── Webhook URL (shown for manual setup only) ── */}
            {waSettingsMode === 'manual' && (
              <div style={{ marginBottom: '14px', padding: '12px', background: 'var(--bg-page)', borderRadius: '8px', border: '1px solid var(--card-border)' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Webhook URL</div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input readOnly value={webhookUrl} style={{ flex: 1, padding: '8px 10px', background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '7px', color: 'var(--text-muted)', fontSize: '11px', outline: 'none' }} />
                  <button onClick={() => { navigator.clipboard.writeText(webhookUrl); alert('Copied!'); }}
                    style={{ padding: '8px 14px', background: '#5B4FE8', color: 'white', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '12px' }}>Copy</button>
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '6px' }}>
                  Verify token: <code style={{ background: 'var(--bg-card)', padding: '1px 6px', borderRadius: '4px', color: 'var(--text-secondary)' }}>{editingBot.businessName}_{new Date().getFullYear()}</code>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── Facebook Page connection ───────────────────────── */}
        {editingBot.botType === 'facebook' && (
          <div style={{ marginBottom: '18px', padding: '14px', background: 'var(--bg-page)', borderRadius: '10px', border: '1px solid var(--card-border)' }}>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>Facebook Page Connection</div>
            {fbReconnectStatus === 'done' ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '20px' }}>✅</span>
                <div style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: '600' }}>Page reconnected — click Save to apply.</div>
              </div>
            ) : fbReconnectStatus === 'pages' && fbReconnectPages.length > 0 ? (
              <div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Select the Facebook Page to connect:</div>
                {fbReconnectPages.map(p => (
                  <button key={p.id} onClick={() => handleSelectFbReconnectPage(p)}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', marginBottom: '6px', background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '13px' }}>
                    <div style={{ fontWeight: '600' }}>{p.name}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>ID: {p.id}</div>
                  </button>
                ))}
              </div>
            ) : (
              <>
                <button onClick={connectFacebookPage} disabled={fbReconnecting}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#1877F2', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 16px', cursor: fbReconnecting ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '600', opacity: fbReconnecting ? 0.7 : 1 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  {fbReconnecting ? 'Connecting…' : 'Reconnect Facebook Page'}
                </button>
                <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '8px' }}>
                  Use this if the bot stops receiving messages, or after new permissions are added to the app — this re-authorizes the connection and picks up the latest access scopes.
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Email Bot credentials ─────────────────────────── */}
        {(editingBot as any).botType === 'email' && (
          <>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>Email Credentials</div>
            {[
              { label: 'Email Address', key: 'emailUser', type: 'email', placeholder: 'you@example.com' },
              { label: 'App Password', key: 'emailPass', type: 'password', placeholder: 'App password (not your login password)' },
              { label: 'Display Name', key: 'emailFrom', type: 'text', placeholder: 'My Business <you@example.com>' },
              { label: 'IMAP Host', key: 'imapHost', type: 'text', placeholder: 'imap.gmail.com' },
              { label: 'SMTP Host', key: 'smtpHost', type: 'text', placeholder: 'smtp.gmail.com' },
            ].map(field => (
              <div key={field.key} style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>{field.label}</label>
                <input type={field.type} value={(editingBot as any)[field.key] || ''} onChange={e => setEditingBot({ ...editingBot, [field.key]: e.target.value } as any)} placeholder={field.placeholder}
                  style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
              </div>
            ))}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>IMAP Port</label>
                <input type="number" value={(editingBot as any).imapPort || 993} onChange={e => setEditingBot({ ...editingBot, imapPort: Number(e.target.value) } as any)}
                  style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>SMTP Port</label>
                <input type="number" value={(editingBot as any).smtpPort || 587} onChange={e => setEditingBot({ ...editingBot, smtpPort: Number(e.target.value) } as any)}
                  style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px', marginTop: '4px' }}>AI Configuration</div>
          </>
        )}

        {/* ── AI Bot config ─────────────────────────────────── */}
        {(editingBot.botType === 'ai' || (editingBot as any).botType === 'email' || (editingBot.botType === 'facebook' && (editingBot as any).fbMode === 'comments')) && (
          <>
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Tone</label>
              <select
                value={(editingBot as any).aiConfig?.tone || 'friendly'}
                onChange={e => setEditingBot({ ...editingBot, aiConfig: { ...(editingBot as any).aiConfig, tone: e.target.value } } as any)}
                style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
              >
                {TONE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '4px' }}>
                {TONE_OPTIONS.find(t => t.value === ((editingBot as any).aiConfig?.tone || 'friendly'))?.hint}
              </div>
            </div>
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
                Custom System Instructions <span style={{ color: 'var(--text-faint)', textTransform: 'none', fontWeight: '400' }}>(optional)</span>
              </label>
              <textarea
                value={(editingBot as any).aiConfig?.systemInstruction || ''}
                onChange={e => setEditingBot({ ...editingBot, aiConfig: { ...(editingBot as any).aiConfig, systemInstruction: e.target.value } } as any)}
                rows={3}
                placeholder="e.g. Always reply in Shona. Never discuss competitor products. Always end with asking if there is anything else you can help with."
                style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
              />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                {[
                  { label: 'Sales focus', text: 'Always highlight the value and benefits of our products. Guide the customer toward making a purchase or booking. When appropriate, mention any current offers or promotions. End every reply by inviting them to take the next step.' },
                  { label: 'Customer support', text: 'Be empathetic and solution-focused. Always acknowledge the customer\'s concern before answering. If you cannot resolve an issue, offer to escalate it to a human agent. Never leave a customer without a next step.' },
                  { label: 'Local language', text: 'If the customer writes in Shona or Ndebele, reply in the same language. If they write in English, reply in English. Always be respectful and culturally appropriate.' },
                  { label: 'Strict KB only', text: 'Only answer questions that are directly answered in the knowledge base. For anything outside the knowledge base, politely say you don\'t have that information and ask them to contact the team directly. Never guess or make up information.' },
                  { label: 'Appointment booking', text: 'Your main goal is to help customers book an appointment or schedule a consultation. Collect their name, preferred date and time, and the service they need. Confirm all details before ending the conversation.' },
                  { label: 'Professional email', text: 'Always write in a formal, professional email tone. Use proper greetings and sign-offs. Structure responses with clear paragraphs. Avoid slang or casual language. Sign off with the business name.' },
                ].map(p => (
                  <button key={p.label} type="button"
                    onClick={() => setEditingBot({ ...editingBot, aiConfig: { ...(editingBot as any).aiConfig, systemInstruction: p.text } } as any)}
                    style={{ padding: '4px 10px', borderRadius: '20px', border: '1px solid var(--card-border)', background: 'var(--bg-element)', color: 'var(--text-secondary)', fontSize: '11px', cursor: 'pointer', fontWeight: '500' }}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Knowledge Base Dataset
                </label>
                <Link to="/datasets" target="_blank" style={{ fontSize: '11px', color: '#818cf8', textDecoration: 'none' }}>Manage datasets ↗</Link>
              </div>
              {settingsDatasets.length === 0 ? (
                <div style={{ background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '8px', padding: '12px', fontSize: '12px', color: 'var(--text-muted)' }}>
                  No datasets uploaded yet.{' '}
                  <Link to="/datasets" target="_blank" style={{ color: '#818cf8' }}>Upload one</Link>
                  {' '}(opens in a new tab), then reopen Settings to select it.
                </div>
              ) : (
                <select
                  value={(editingBot as any).aiConfig?.datasetId || ''}
                  onChange={e => setEditingBot({ ...editingBot, aiConfig: { ...(editingBot as any).aiConfig, datasetId: e.target.value || null } } as any)}
                  style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
                >
                  <option value="">— No dataset (use inline text below) —</option>
                  {settingsDatasets.map(d => <option key={d.id} value={d.id}>{d.name} ({d.format})</option>)}
                </select>
              )}
            </div>
            <div style={{ marginBottom: '14px' }}>
              <button type="button" onClick={() => setShowLegacyKB(v => !v)}
                style={{ background: 'none', border: 'none', color: 'var(--text-faint)', cursor: 'pointer', fontSize: '11px', padding: 0, marginBottom: '6px' }}>
                {showLegacyKB ? '▾' : '▸'} {(editingBot as any).aiConfig?.knowledgeBase ? 'Edit inline text (legacy)' : 'Or type inline text instead'}
              </button>
              {showLegacyKB && (
                <>
                  <div style={{ fontSize: '12px', color: 'var(--text-faint)', marginBottom: '6px' }}>
                    If a dataset is selected above, it takes priority. Inline text is a fallback — upload a dataset for better organisation.
                  </div>
                  <textarea
                    value={(editingBot as any).aiConfig?.knowledgeBase || ''}
                    onChange={e => setEditingBot({ ...editingBot, aiConfig: { ...(editingBot as any).aiConfig, knowledgeBase: e.target.value } } as any)}
                    rows={8}
                    placeholder="Paste your business FAQs, pricing, policies here as a fallback..."
                    style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', resize: 'vertical', fontFamily: 'monospace', boxSizing: 'border-box' }}
                  />
                  <div style={{ fontSize: '11px', marginTop: '4px', color: ((editingBot as any).aiConfig?.knowledgeBase || '').length > 8000 ? '#f59e0b' : 'var(--text-faint)' }}>
                    {((editingBot as any).aiConfig?.knowledgeBase || '').length.toLocaleString()} characters
                    {((editingBot as any).aiConfig?.knowledgeBase || '').length > 8000 ? ' — large: only the most relevant sections are sent per reply' : ''}
                  </div>
                </>
              )}
            </div>
            {/* Channel-specific credentials */}
            {(editingBot as any).aiChannel === 'whatsapp' && editingBot.botType === 'ai' && (<>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px', marginTop: '4px' }}>WhatsApp Credentials</div>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Phone Number ID</label>
                <input value={(editingBot as any).phoneNumberId || ''} onChange={e => setEditingBot({ ...editingBot, phoneNumberId: e.target.value } as any)} placeholder="1234567890"
                  style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Access Token</label>
                <input type="password" value={(editingBot as any).accessToken || ''} onChange={e => setEditingBot({ ...editingBot, accessToken: e.target.value } as any)} placeholder="Already connected — leave blank to keep, or paste a new token to replace it"
                  style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: '14px', padding: '12px', background: 'var(--bg-page)', borderRadius: '8px', border: '1px solid var(--card-border)' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Webhook URL</div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input readOnly value={webhookUrl} style={{ flex: 1, padding: '8px 10px', background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '7px', color: 'var(--text-muted)', fontSize: '11px', outline: 'none' }} />
                  <button onClick={() => { navigator.clipboard.writeText(webhookUrl); alert('Copied!'); }}
                    style={{ padding: '8px 14px', background: '#5B4FE8', color: 'white', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '12px' }}>Copy</button>
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '6px' }}>
                  Verify token: <code style={{ background: 'var(--bg-card)', padding: '1px 6px', borderRadius: '4px', color: 'var(--text-secondary)' }}>{editingBot.businessName}_{new Date().getFullYear()}</code>
                </div>
              </div>
            </>)}
            {(editingBot as any).aiChannel === 'facebook' && (<>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px', marginTop: '4px' }}>Facebook Page Credentials</div>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Facebook Page ID</label>
                <input value={(editingBot as any).fbPageId || ''} onChange={e => setEditingBot({ ...editingBot, fbPageId: e.target.value } as any)} placeholder="123456789012345"
                  style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Page Access Token</label>
                <input type="password" value={(editingBot as any).accessToken || ''} onChange={e => setEditingBot({ ...editingBot, accessToken: e.target.value } as any)} placeholder="Already connected — leave blank to keep, or paste a new token to replace it"
                  style={{ width: '100%', padding: '10px 12px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
              </div>
            </>)}
          </>
        )}

        {(editingBot.botType === 'website' || (editingBot.botType === 'ai' && (editingBot as any).aiChannel === 'website')) && (
          <>
            <div style={{ marginBottom: '14px' }}>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Embed code</div>
              <div style={{ background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '8px', padding: '12px' }}>
                <code style={{ fontSize: '11px', color: '#818cf8', wordBreak: 'break-all' }}>
                  {`<script src="${widgetURL}/widget.js" data-bot-id="${editingBot.id}"${API_BASE ? ` data-api-url="${API_BASE}"` : ''}></script>`}
                </code>
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '5px' }}>Paste this before the {'</body>'} tag on your website</div>
            </div>
            <div style={{ marginBottom: '14px' }}>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Allowed websites</div>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <input type="text" value={newWebsiteInput} onChange={e => setNewWebsiteInput(e.target.value)} placeholder="mybusiness.com"
                  style={{ flex: 1, padding: '8px 10px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '7px', color: 'var(--text-primary)', fontSize: '12px', outline: 'none' }} />
                <button onClick={() => {
                  if (newWebsiteInput.trim() && !editingBot.allowedWebsites?.includes(newWebsiteInput.trim())) {
                    setEditingBot({ ...editingBot, allowedWebsites: [...(editingBot.allowedWebsites || []), newWebsiteInput.trim()] });
                    setNewWebsiteInput('');
                  }
                }} style={{ padding: '8px 14px', background: '#5B4FE8', color: 'white', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '12px' }}>Add</button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {(editingBot.allowedWebsites || []).map(w => (
                  <span key={w} style={{ background: 'var(--bg-element)', padding: '3px 10px', borderRadius: '14px', fontSize: '11px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {w}
                    <button onClick={() => setEditingBot({ ...editingBot, allowedWebsites: editingBot.allowedWebsites?.filter(x => x !== w) })}
                      style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 0, lineHeight: 1, fontSize: '12px' }}>×</button>
                  </span>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ── Payment Methods — hidden for AI and email bots ── */}
        {editingBot.botType !== 'ai' && (editingBot as any).botType !== 'email' && <div style={{ marginBottom: '16px', marginTop: '8px' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>
            💳 Payment Methods
          </div>
          {/* Paynow */}
          <div style={{ background: 'var(--bg-element)', borderRadius: '8px', padding: '10px', marginBottom: '8px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '8px' }}>Paynow (Zimbabwe)</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '6px' }}>
              <input value={(editingBot as any).paymentMethods?.paynow?.integrationId || ''}
                onChange={e => setEditingBot({ ...editingBot, paymentMethods: { ...(editingBot as any).paymentMethods, paynow: { ...(editingBot as any).paymentMethods?.paynow, integrationId: e.target.value } } } as any)}
                placeholder="Integration ID" style={{ padding: '7px 10px', background: 'var(--bg-page)', border: '1px solid #2d3548', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '12px' }} />
              <input value={(editingBot as any).paymentMethods?.paynow?.integrationKey || ''}
                onChange={e => setEditingBot({ ...editingBot, paymentMethods: { ...(editingBot as any).paymentMethods, paynow: { ...(editingBot as any).paymentMethods?.paynow, integrationKey: e.target.value } } } as any)}
                placeholder="Integration Key" style={{ padding: '7px 10px', background: 'var(--bg-page)', border: '1px solid #2d3548', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '12px' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '6px' }}>
              <input value={(editingBot as any).paymentMethods?.paynow?.testEmail || ''}
                onChange={e => setEditingBot({ ...editingBot, paymentMethods: { ...(editingBot as any).paymentMethods, paynow: { ...(editingBot as any).paymentMethods?.paynow, testEmail: e.target.value } } } as any)}
                placeholder="Test email (for test mode)" style={{ padding: '7px 10px', background: 'var(--bg-page)', border: '1px solid #2d3548', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '12px' }} />
              <select value={(editingBot as any).paymentMethods?.paynow?.mode || 'test'}
                onChange={e => setEditingBot({ ...editingBot, paymentMethods: { ...(editingBot as any).paymentMethods, paynow: { ...(editingBot as any).paymentMethods?.paynow, mode: e.target.value } } } as any)}
                style={{ padding: '7px 10px', background: 'var(--bg-page)', border: '1px solid #2d3548', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '12px' }}>
                <option value="test">Test</option>
                <option value="live">Live</option>
              </select>
            </div>
          </div>
          {/* Cash */}
          <div style={{ background: 'var(--bg-element)', borderRadius: '8px', padding: '10px', marginBottom: '8px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '8px' }}>Cash</div>
            <input value={(editingBot as any).paymentMethods?.cash?.venue || ''}
              onChange={e => setEditingBot({ ...editingBot, paymentMethods: { ...(editingBot as any).paymentMethods, cash: { ...(editingBot as any).paymentMethods?.cash, venue: e.target.value } } } as any)}
              placeholder="Venue / location (e.g. Head Office, 123 Main St)" style={{ width: '100%', padding: '7px 10px', background: 'var(--bg-page)', border: '1px solid #2d3548', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '12px', marginBottom: '6px', boxSizing: 'border-box' }} />
            <input value={(editingBot as any).paymentMethods?.cash?.instructions || ''}
              onChange={e => setEditingBot({ ...editingBot, paymentMethods: { ...(editingBot as any).paymentMethods, cash: { ...(editingBot as any).paymentMethods?.cash, instructions: e.target.value } } } as any)}
              placeholder="Instructions (e.g. Ask for the cashier at reception)" style={{ width: '100%', padding: '7px 10px', background: 'var(--bg-page)', border: '1px solid #2d3548', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '12px', boxSizing: 'border-box' }} />
          </div>
          {/* Bank */}
          <div style={{ background: 'var(--bg-element)', borderRadius: '8px', padding: '10px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '8px' }}>Bank Transfer</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '6px' }}>
              <input value={(editingBot as any).paymentMethods?.bank?.bankName || ''}
                onChange={e => setEditingBot({ ...editingBot, paymentMethods: { ...(editingBot as any).paymentMethods, bank: { ...(editingBot as any).paymentMethods?.bank, bankName: e.target.value } } } as any)}
                placeholder="Bank name" style={{ padding: '7px 10px', background: 'var(--bg-page)', border: '1px solid #2d3548', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '12px' }} />
              <input value={(editingBot as any).paymentMethods?.bank?.accountName || ''}
                onChange={e => setEditingBot({ ...editingBot, paymentMethods: { ...(editingBot as any).paymentMethods, bank: { ...(editingBot as any).paymentMethods?.bank, accountName: e.target.value } } } as any)}
                placeholder="Account name" style={{ padding: '7px 10px', background: 'var(--bg-page)', border: '1px solid #2d3548', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '12px' }} />
              <input value={(editingBot as any).paymentMethods?.bank?.accountNumber || ''}
                onChange={e => setEditingBot({ ...editingBot, paymentMethods: { ...(editingBot as any).paymentMethods, bank: { ...(editingBot as any).paymentMethods?.bank, accountNumber: e.target.value } } } as any)}
                placeholder="Account number" style={{ padding: '7px 10px', background: 'var(--bg-page)', border: '1px solid #2d3548', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '12px' }} />
              <input value={(editingBot as any).paymentMethods?.bank?.branch || ''}
                onChange={e => setEditingBot({ ...editingBot, paymentMethods: { ...(editingBot as any).paymentMethods, bank: { ...(editingBot as any).paymentMethods?.bank, branch: e.target.value } } } as any)}
                placeholder="Branch (optional)" style={{ padding: '7px 10px', background: 'var(--bg-page)', border: '1px solid #2d3548', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '12px' }} />
            </div>
            <input value={(editingBot as any).paymentMethods?.bank?.instructions || ''}
              onChange={e => setEditingBot({ ...editingBot, paymentMethods: { ...(editingBot as any).paymentMethods, bank: { ...(editingBot as any).paymentMethods?.bank, instructions: e.target.value } } } as any)}
              placeholder="Additional instructions (optional)" style={{ width: '100%', padding: '7px 10px', background: 'var(--bg-page)', border: '1px solid #2d3548', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '12px', boxSizing: 'border-box' }} />
          </div>
        </div>}

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', borderTop: '1px solid var(--bg-element)', paddingTop: '16px', marginTop: '8px' }}>
          <button onClick={onClose}
            style={{ padding: '9px 18px', background: 'var(--bg-element)', color: 'var(--text-muted)', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
          <button onClick={handleUpdateBot}
            style={{ padding: '9px 22px', background: 'linear-gradient(135deg, #5B4FE8, #7c3aed)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>Save changes</button>
        </div>
        <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid var(--bg-element)' }}>
          <button onClick={handleDeleteBot}
            style={{ width: '100%', padding: '9px', background: '#ef444410', color: '#ef4444', border: '1px solid #ef444430', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>Delete bot</button>
        </div>
      </div>
    </div>
  );
}
